import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

// Sub-components
import CalendarHeader from "../../../components/Admin/AdminOrderCalenderServices/CalendarHeader";
import CalendarToolbar from "../../../components/Admin/AdminOrderCalenderServices/CalendarToolbar";
import CalendarGrid from "../../../components/Admin/AdminOrderCalenderServices/CalendarGrid";
import TodayTimeline from "../../../components/Admin/AdminOrderCalenderServices/TodayTimeline";
import WeeklyTimeline from "../../../components/Admin/AdminOrderCalenderServices/WeeklyTimeline";
import ServiceDrawer from "../../../components/Admin/AdminOrderCalenderServices/ServiceDrawer";
import ServiceManagementModal from "../../../components/Admin/AdminOrderCalenderServices/ServiceManagementModal";
import CreateBookingScreen from "../Services/CreateBookingScreen";

// API Actions & Types
import {
  useAssignEmployeeMutation,
  useLazyGetAllServiceQuery,
  useLazyGetBookingDetailsByIdQuery,
  useLazyGetDailyTimelineQuery,
  useLazyGetEmployeeByServiceIdQuery,
  useLazyGetWeekOrMonthTimelineQuery,
  useRescheduleBookingMutation,
  useUpdateDurationMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import {
  BookingDetails,
  IserviceResponse,
  TimelineBooking,
} from "../../../types/Response/AdminResponse";

import {
  IAssignEmployeeRequest,
  IRescheduleBookingRequest,
  IUpdateBookingDurationRequest,
} from "../../../types/Request/AdminRequest";

// Helper: ISO Date String YYYY-MM-DD
const formatLocalDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Helper: Format Time String
const formatTime = (timeStr?: string): string => {
  if (!timeStr) return "";

  const parts = timeStr.split(":");

  if (parts.length >= 2) {
    let hour = parseInt(parts[0], 10);
    const minute = parts[1];

    if (isNaN(hour)) return timeStr;

    const ampm = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;
    hour = hour ? hour : 12;

    return `${hour}:${minute} ${ampm}`;
  }

  return timeStr;
};

// Helper: Format Duration
const formatDuration = (totalMinutes?: number): string => {
  if (!totalMinutes || totalMinutes <= 0) return "0m";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
};

// Helper: Extract Add-Ons
const extractAddOnNames = (serviceDataJson?: string): string => {
  if (!serviceDataJson) return "";

  try {
    const parsed = JSON.parse(serviceDataJson);

    if (
      parsed &&
      Array.isArray(parsed.selectedAddOns) &&
      parsed.selectedAddOns.length > 0
    ) {
      return parsed.selectedAddOns
        .map((item: any) => item.name)
        .filter(Boolean)
        .join(", ");
    }
  } catch (e) {
    console.error("Failed to parse serviceDataJson:", e);
  }

  return "";
};

// Helper: Parse Working Hour
const parseWorkingHour = (
  timeStr?: string,
  defaultHour: number = 9
): number => {
  if (!timeStr) return defaultHour;

  const hour = parseInt(timeStr.split(":")[0], 10);

  return isNaN(hour) ? defaultHour : hour;
};

// Helper: Process Booking
const processBookingItem = (booking: any) => {
  const addOnsText = extractAddOnNames(booking.serviceDataJson);

  const rawTimeMatch = booking.startTime
    ? booking.startTime.substring(0, 5)
    : "";

  return {
    ...booking,
    rawStartTime: rawTimeMatch,
    formattedStartTime: formatTime(booking.startTime),
    formattedEndTime: formatTime(booking.endTime),
    formattedDuration: formatDuration(booking.durationMinutes),
    addOnsText,
    displayPetWithAddons: addOnsText
      ? `${booking.petName} (+ ${addOnsText})`
      : booking.petName,
  };
};

const AdminBookingService: React.FC = () => {
  const [searchParams] = useSearchParams();

  const queryServiceId = searchParams.get("serviceId");
  const queryDate = searchParams.get("date");

  // API Hooks
  const [
    getAllServices,
    { isLoading: isLoadingServices },
  ] = useLazyGetAllServiceQuery();

  const [
    getDailyTimeline,
    { data: dailyTimelineData },
  ] = useLazyGetDailyTimelineQuery();

  const [
    getWeekOrMonthTimeline,
    { data: weekMonthTimelineData },
  ] = useLazyGetWeekOrMonthTimelineQuery();

  const [getBookingDetails] =
    useLazyGetBookingDetailsByIdQuery();

  const [getEmployeeByServiceId] =
    useLazyGetEmployeeByServiceIdQuery();

  const [
    assignEmployee,
    { isLoading: isAssigning },
  ] = useAssignEmployeeMutation();

  const [
    rescheduleBooking,
    { isLoading: isRescheduling },
  ] = useRescheduleBookingMutation();

  const [
    updateDuration,
    { isLoading: isUpdatingDuration },
  ] = useUpdateDurationMutation();

  // Local Component States
  const [services, setServices] = useState<IserviceResponse[]>([]);

  const [selectedServiceId, setSelectedServiceId] =
    useState<number | null>(
      queryServiceId ? Number(queryServiceId) : null
    );

  const [calendarDate, setCalendarDate] = useState<Date>(
    queryDate ? new Date(queryDate) : new Date()
  );

  const [selectedViewDate, setSelectedViewDate] =
    useState<Date>(
      queryDate ? new Date(queryDate) : new Date()
    );

  const [viewMode, setViewMode] = useState<
    "today" | "weekly" | "monthly"
  >("today");

  // Drawer & Management Modal States
  const [selectedDate, setSelectedDate] =
    useState<Date | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] =
    useState(false);

  const [drawerServices, setDrawerServices] =
    useState<any[]>([]);

  const [selectedService, setSelectedService] =
    useState<any | null>(null);

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  // Create Booking
  const [isCreateBookingOpen, setIsCreateBookingOpen] =
    useState(false);

  const [prefilledSlot, setPrefilledSlot] =
    useState<{
      date: string;
      startTime: string;
    } | null>(null);

  // Action Modals
  const [
    selectedBookingForAction,
    setSelectedBookingForAction,
  ] = useState<any | null>(null);

  const [isAssignModalOpen, setIsAssignModalOpen] =
    useState(false);

  const [
    isRescheduleModalOpen,
    setIsRescheduleModalOpen,
  ] = useState(false);

  const [
    isDurationModalOpen,
    setIsDurationModalOpen,
  ] = useState(false);

  // Assign Employee
  const [employeesList, setEmployeesList] =
    useState<any[]>([]);

  const [selectedEmployeeId, setSelectedEmployeeId] =
    useState<number | "">("");

  // Reschedule & Duration
  const [rescheduleDate, setRescheduleDate] =
    useState("");

  const [rescheduleTime, setRescheduleTime] =
    useState("");

  const [newEndTime, setNewEndTime] =
    useState("");

  const [actionRemarks, setActionRemarks] =
    useState("");

  // Date Range Calculations
  const getWeekRange = (date: Date) => {
    const monday = new Date(date);

    const day = monday.getDay();

    const diff = day === 0 ? -6 : 1 - day;

    monday.setDate(monday.getDate() + diff);

    const sunday = new Date(monday);

    sunday.setDate(monday.getDate() + 6);

    return {
      start: monday,
      end: sunday,
    };
  };

  const weekRange = getWeekRange(selectedViewDate);

  const month = calendarDate.toLocaleString(
    "default",
    {
      month: "long",
    }
  );

  const year = calendarDate.getFullYear();

  const headerTitle =
    viewMode === "monthly"
      ? `${month} ${year}`
      : viewMode === "weekly"
      ? `${weekRange.start.getDate()} ${weekRange.start.toLocaleString(
          "default",
          {
            month: "short",
          }
        )} - ${weekRange.end.getDate()} ${weekRange.end.toLocaleString(
          "default",
          {
            month: "short",
          }
        )} ${weekRange.end.getFullYear()}`
      : selectedViewDate.toLocaleDateString(
          "default",
          {
            weekday: "long",
            day: "numeric",
            month: "short",
            year: "numeric",
          }
        );

  const headerSubTitle =
    viewMode === "monthly"
      ? "View all bookings for the selected month."
      : viewMode === "weekly"
      ? "View bookings for the current week."
      : "View today's scheduled services.";

  // ============================================================
  // LOAD SERVICES
  // Grooming is ALWAYS FIRST
  // ============================================================
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response =
          await getAllServices().unwrap();

        const list = (
          Array.isArray(response)
            ? response
            : []
        ).filter(
          (service) => service.isActive
        );

        // Always keep Grooming at the top
        const sortedServices = [...list].sort(
          (a, b) => {
            const aIsGrooming =
              a.name?.trim().toLowerCase() ===
              "grooming";

            const bIsGrooming =
              b.name?.trim().toLowerCase() ===
              "grooming";

            if (
              aIsGrooming &&
              !bIsGrooming
            ) {
              return -1;
            }

            if (
              !aIsGrooming &&
              bIsGrooming
            ) {
              return 1;
            }

            return 0;
          }
        );

        setServices(sortedServices);

        if (
          sortedServices.length > 0 &&
          !selectedServiceId
        ) {
          setSelectedServiceId(
            queryServiceId
              ? Number(queryServiceId)
              : sortedServices[0].id
          );
        }
      } catch (err) {
        console.error(
          "Failed to load services:",
          err
        );
      }
    };

    fetchServices();
  }, [
    getAllServices,
    queryServiceId,
    selectedServiceId,
  ]);

  // ============================================================
  // FETCH TIMELINE DATA
  // ============================================================
  useEffect(() => {
    if (!selectedServiceId) return;

    if (viewMode === "today") {
      getDailyTimeline({
        ServiceId: selectedServiceId,
        bookingDate:
          formatLocalDateISO(
            selectedViewDate
          ),
      });
    } else if (
      viewMode === "weekly"
    ) {
      getWeekOrMonthTimeline({
        serviceId: selectedServiceId,
        fromDate:
          formatLocalDateISO(
            weekRange.start
          ),
        toDate:
          formatLocalDateISO(
            weekRange.end
          ),
      });
    } else if (
      viewMode === "monthly"
    ) {
      const startOfMonth = new Date(
        calendarDate.getFullYear(),
        calendarDate.getMonth(),
        1
      );

      const endOfMonth = new Date(
        calendarDate.getFullYear(),
        calendarDate.getMonth() + 1,
        0
      );

      getWeekOrMonthTimeline({
        serviceId: selectedServiceId,
        fromDate:
          formatLocalDateISO(
            startOfMonth
          ),
        toDate:
          formatLocalDateISO(
            endOfMonth
          ),
      });
    }
  }, [
    selectedServiceId,
    viewMode,
    selectedViewDate,
    calendarDate,
    getDailyTimeline,
    getWeekOrMonthTimeline,
  ]);

  // ============================================================
  // FETCH EMPLOYEES
  // ============================================================
  useEffect(() => {
    if (
      isAssignModalOpen &&
      selectedBookingForAction?.serviceId
    ) {
      getEmployeeByServiceId(
        selectedBookingForAction.serviceId
      )
        .unwrap()
        .then((res) => {
          setEmployeesList(
            Array.isArray(res) ? res : []
          );
        })
        .catch((err) => {
          console.error(
            "Failed to fetch employees:",
            err
          );

          setEmployeesList([]);
        });
    }
  }, [
    isAssignModalOpen,
    selectedBookingForAction,
    getEmployeeByServiceId,
  ]);

  // ============================================================
  // WORKING HOURS
  // ============================================================
  const workingStartTime =
    dailyTimelineData?.data
      ?.workingStartTime ||
    "09:00:00";

  const workingEndTime =
    dailyTimelineData?.data
      ?.workingEndTime ||
    "18:00:00";

  // ============================================================
  // TODAY SERVICES
  // ============================================================
  const rawTodayServices =
    dailyTimelineData?.data?.bookings ||
    [];

  const todayServices =
    rawTodayServices.map(
      processBookingItem
    );

  // ============================================================
  // WEEK / MONTH DATA
  // ============================================================
  const processedWeekMonthData =
    React.useMemo(() => {
      if (!weekMonthTimelineData?.data) {
        return weekMonthTimelineData;
      }

      const days =
        weekMonthTimelineData.data.days ||
        [];

      return {
        ...weekMonthTimelineData,
        data: {
          ...weekMonthTimelineData.data,
          days: days.map(
            (dayItem: any) => ({
              ...dayItem,
              bookings: (
                dayItem.bookings || []
              ).map(
                processBookingItem
              ),
            })
          ),
        },
      };
    }, [weekMonthTimelineData]);

  // ============================================================
  // EMPTY SLOT
  // ============================================================
  const handleEmptySlotClick = (
    timeString: string,
    dateStr?: string
  ) => {
    setPrefilledSlot({
      date:
        dateStr ||
        formatLocalDateISO(
          selectedViewDate
        ),
      startTime:
        formatTime(timeString),
    });

    setIsCreateBookingOpen(true);
  };

  // ============================================================
  // ASSIGN EMPLOYEE
  // ============================================================
  const handleAssignSubmit = async () => {
    if (
      !selectedBookingForAction ||
      !selectedEmployeeId
    ) {
      alert(
        "Please select a staff member."
      );
      return;
    }

    const selectedEmp =
      employeesList.find(
        (e) =>
          e.employeeId ===
          Number(
            selectedEmployeeId
          )
      );

    try {
      const payload: IAssignEmployeeRequest =
        {
          bookingId:
            selectedBookingForAction.bookingId,

          employeeId:
            Number(
              selectedEmployeeId
            ),

          employeeName:
            selectedEmp?.name || "",
        };

      await assignEmployee(
        payload
      ).unwrap();

      alert(
        "Employee assigned successfully!"
      );

      setIsAssignModalOpen(false);
      setSelectedEmployeeId("");
    } catch {
      alert(
        "Failed to assign employee."
      );
    }
  };

  // ============================================================
  // RESCHEDULE
  // ============================================================
  const handleRescheduleSubmit =
    async () => {
      if (
        !selectedBookingForAction ||
        !rescheduleDate ||
        !rescheduleTime
      ) {
        return;
      }

      try {
        const payload: IRescheduleBookingRequest =
          {
            bookingId:
              selectedBookingForAction.bookingId,

            bookingDate:
              rescheduleDate,

            startTime:
              rescheduleTime,

            remarks:
              actionRemarks,
          };

        await rescheduleBooking(
          payload
        ).unwrap();

        alert(
          "Booking rescheduled successfully!"
        );

        setIsRescheduleModalOpen(
          false
        );

        setActionRemarks("");
      } catch {
        alert(
          "Failed to reschedule booking."
        );
      }
    };

  // ============================================================
  // UPDATE DURATION
  // ============================================================
  const handleDurationSubmit =
    async () => {
      if (
        !selectedBookingForAction ||
        !newEndTime
      ) {
        return;
      }

      try {
        const payload: IUpdateBookingDurationRequest =
          {
            bookingId:
              selectedBookingForAction.bookingId,

            newEndTime:
              newEndTime,

            remarks:
              actionRemarks,
          };

        await updateDuration(
          payload
        ).unwrap();

        alert(
          "Duration updated successfully!"
        );

        setIsDurationModalOpen(
          false
        );

        setActionRemarks("");
      } catch {
        alert(
          "Failed to update duration."
        );
      }
    };

  // ============================================================
  // HANDLERS
  // ============================================================
  const handlers = {
    onDateClick: (date: Date) => {
      setSelectedDate(date);

      const dateStr =
        formatLocalDateISO(date);

      const days =
        processedWeekMonthData?.data
          ?.days || [];

      const matchedDay =
        days.find(
          (d: any) =>
            d.date === dateStr
        );

      setDrawerServices(
        matchedDay?.bookings || []
      );

      setIsDrawerOpen(true);
    },

    onDrawerClose: () => {
      setIsDrawerOpen(false);
    },

    onShowMore: (
      selectedDate: Date,
      servicesList: any[]
    ) => {
      setSelectedDate(
        selectedDate
      );

      setDrawerServices(
        servicesList.map(
          processBookingItem
        )
      );

      setIsDrawerOpen(true);
    },

    onServiceClick: async (
      service: any
    ) => {
      const processed =
        processBookingItem(
          service
        );

      setSelectedService(
        processed
      );

      if (service.bookingId) {
        try {
          const detailRes =
            await getBookingDetails(
              service.bookingId
            ).unwrap();

          if (detailRes?.data) {
            setSelectedService(
              processBookingItem(
                detailRes.data
              )
            );
          }
        } catch (e) {
          console.error(
            "Failed to fetch booking details:",
            e
          );
        }
      }

      setIsModalOpen(true);
    },

    onModalClose: () => {
      setSelectedService(null);
      setIsModalOpen(false);
    },
  };

  // ============================================================
  // CREATE BOOKING SCREEN
  // ============================================================
  if (isCreateBookingOpen) {
    return (
      <div>
        <div className="p-4 bg-white border-b flex justify-between items-center">
          <button
            onClick={() =>
              setIsCreateBookingOpen(
                false
              )
            }
            className="flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Calendar
          </button>
        </div>

        <CreateBookingScreen
          initialServiceId={
            selectedServiceId ||
            undefined
          }
          initialDate={
            prefilledSlot?.date
          }
          initialStartTime={
            prefilledSlot?.startTime
          }
          onBack={() =>
            setIsCreateBookingOpen(
              false
            )
          }
          onSuccess={() => {
            setIsCreateBookingOpen(
              false
            );

            if (selectedServiceId) {
              getDailyTimeline({
                ServiceId:
                  selectedServiceId,

                bookingDate:
                  formatLocalDateISO(
                    selectedViewDate
                  ),
              });
            }
          }}
        />
      </div>
    );
  }

  // ============================================================
  // MAIN UI
  // ============================================================
  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen font-nunito">


    {/* ======================================================
    SERVICE SELECTION
    ====================================================== */}
<div className="mb-5">
  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">

    {isLoadingServices ? (
      [1, 2, 3].map((n) => (
        <div
          key={n}
          className="
            flex-shrink-0
            w-[120px]
            sm:w-[140px]
            h-[82px]
            rounded-xl
            bg-gray-200
            animate-pulse
          "
        />
      ))
    ) : (
      services.map((srv) => {
        const isSelected =
          selectedServiceId === srv.id;

        return (
          <button
            key={srv.id}
            type="button"
            onClick={() =>
              setSelectedServiceId(srv.id)
            }
            className={`
              flex
              flex-col
              items-center
              justify-center
              gap-2
              flex-shrink-0

              w-[120px]
              sm:w-[140px]
              md:w-[150px]

              h-[82px]
              sm:h-[88px]

              rounded-xl
              border

              transition-all
              duration-200

              cursor-pointer

              ${
                isSelected
                  ? "bg-yellow-500 border-yellow-500 text-white shadow-md"
                  : "bg-white border-gray-200 text-gray-700 hover:border-yellow-300 hover:shadow-sm"
              }
            `}
          >
            {/* SERVICE IMAGE */}
            <div
              className={`
                w-9
                h-9
                sm:w-10
                sm:h-10
                rounded-full
                overflow-hidden
                flex
                items-center
                justify-center
                flex-shrink-0

                ${
                  isSelected
                    ? "bg-white/20"
                    : "bg-gray-100"
                }
              `}
            >
              {srv.logoUrl ? (
                <img
                  src={srv.logoUrl}
                  alt={srv.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-lg">
                  🐾
                </span>
              )}
            </div>

            {/* SERVICE NAME */}
            <span
              className={`
                text-xs
                sm:text-sm
                font-semibold
                truncate
                max-w-[105px]
                sm:max-w-[125px]
              `}
            >
              {srv.name}
            </span>
          </button>
        );
      })
    )}
  </div>
</div>

      {/* ======================================================
          CALENDAR HEADER
          ====================================================== */}
      <CalendarHeader
        data={{
          viewMode,
          title: headerTitle,
          subTitle:
            headerSubTitle,
        }}
        handlers={{
          onToday: () => {
            if (
              viewMode ===
              "monthly"
            ) {
              setCalendarDate(
                new Date()
              );
            } else {
              setSelectedViewDate(
                new Date()
              );
            }
          },

          onPrevious: () => {
            if (
              viewMode ===
              "monthly"
            ) {
              setCalendarDate(
                new Date(
                  calendarDate.getFullYear(),
                  calendarDate.getMonth() -
                    1,
                  1
                )
              );
            } else if (
              viewMode ===
              "weekly"
            ) {
              setSelectedViewDate(
                new Date(
                  selectedViewDate.getTime() -
                    7 *
                      24 *
                      60 *
                      60 *
                      1000
                )
              );
            } else {
              setSelectedViewDate(
                new Date(
                  selectedViewDate.getTime() -
                    24 *
                      60 *
                      60 *
                      1000
                )
              );
            }
          },

          onNext: () => {
            if (
              viewMode ===
              "monthly"
            ) {
              setCalendarDate(
                new Date(
                  calendarDate.getFullYear(),
                  calendarDate.getMonth() +
                    1,
                  1
                )
              );
            } else if (
              viewMode ===
              "weekly"
            ) {
              setSelectedViewDate(
                new Date(
                  selectedViewDate.getTime() +
                    7 *
                      24 *
                      60 *
                      60 *
                      1000
                )
              );
            } else {
              setSelectedViewDate(
                new Date(
                  selectedViewDate.getTime() +
                    24 *
                      60 *
                      60 *
                      1000
                )
              );
            }
          },
        }}
      />

      {/* ======================================================
          CALENDAR TOOLBAR
          ====================================================== */}
      <CalendarToolbar
        data={{
          viewMode,
        }}
        handlers={{
          onViewModeChange:
            setViewMode,
        }}
      />

      {/* ======================================================
          MONTHLY
          ====================================================== */}
      {viewMode ===
        "monthly" && (
        <CalendarGrid
          currentDate={
            calendarDate
          }
          onDateClick={
            handlers.onDateClick
          }
          timelineData={
            processedWeekMonthData
          }
          onEmptySlotClick={
            handleEmptySlotClick
          }
        />
      )}

      {/* ======================================================
          WEEKLY
          ====================================================== */}
      {viewMode ===
        "weekly" && (
        <WeeklyTimeline
          data={{
            services:
              processedWeekMonthData
                ?.data?.days || [],
            selectedDate:
              selectedViewDate,
          }}
          handlers={{
            onServiceClick:
              handlers.onServiceClick,

            onShowMore:
              handlers.onShowMore,

            onEmptySlotClick:
              handleEmptySlotClick,
          }}
        />
      )}

      {/* ======================================================
          TODAY
          ====================================================== */}
      {viewMode ===
        "today" && (
        <TodayTimeline
          services={
            todayServices
          }
          workingStartTime={
            workingStartTime
          }
          workingEndTime={
            workingEndTime
          }
          handlers={{
            onServiceClick:
              handlers.onServiceClick,

            onEmptySlotClick:
              handleEmptySlotClick,

            onAssignEmployee: (
              booking: any
            ) => {
              setSelectedBookingForAction(
                booking
              );

              setSelectedEmployeeId(
                ""
              );

              setIsAssignModalOpen(
                true
              );
            },

            onReschedule: (
              booking: any
            ) => {
              setSelectedBookingForAction(
                booking
              );

              setRescheduleDate(
                booking.bookingDate ||
                  formatLocalDateISO(
                    selectedViewDate
                  )
              );

              setRescheduleTime(
                formatTime(
                  booking.startTime
                ) || ""
              );

              setIsRescheduleModalOpen(
                true
              );
            },

            onUpdateDuration: (
              booking: any
            ) => {
              setSelectedBookingForAction(
                booking
              );

              setNewEndTime(
                formatTime(
                  booking.endTime
                ) || ""
              );

              setIsDurationModalOpen(
                true
              );
            },
          }}
        />
      )}

      {/* ======================================================
          SERVICE DRAWER
          ====================================================== */}
      <ServiceDrawer
        data={{
          isOpen:
            isDrawerOpen,

          selectedDate:
            selectedDate,

          services:
            drawerServices,
        }}
        handlers={{
          onClose:
            handlers.onDrawerClose,

          onServiceClick:
            handlers.onServiceClick,
        }}
      />

      {/* ======================================================
          SERVICE MANAGEMENT MODAL
          ====================================================== */}
      <ServiceManagementModal
        isOpen={isModalOpen}
        service={
          selectedService
        }
        onClose={
          handlers.onModalClose
        }
        onAssignEmployee={(
          booking
        ) => {
          setSelectedBookingForAction(
            booking
          );

          setSelectedEmployeeId(
            ""
          );

          setIsAssignModalOpen(
            true
          );
        }}
        onReschedule={(
          booking
        ) => {
          setSelectedBookingForAction(
            booking
          );

          setRescheduleDate(
            booking.bookingDate ||
              ""
          );

          setRescheduleTime(
            formatTime(
              booking.startTime
            ) || ""
          );

          setIsRescheduleModalOpen(
            true
          );
        }}
        onUpdateDuration={(
          booking
        ) => {
          setSelectedBookingForAction(
            booking
          );

          setNewEndTime(
            formatTime(
              booking.endTime
            ) || ""
          );

          setIsDurationModalOpen(
            true
          );
        }}
      />

      {/* ======================================================
          ASSIGN EMPLOYEE MODAL
          ====================================================== */}
      {isAssignModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-4">

            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-800">
                Assign Staff Member
              </h3>

              <button
                onClick={() =>
                  setIsAssignModalOpen(
                    false
                  )
                }
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Select Staff Member for{" "}
                  {selectedBookingForAction?.serviceName ||
                    "Service"}
                </label>

                <select
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs bg-white outline-none focus:border-violet-500 cursor-pointer"
                  value={
                    selectedEmployeeId
                  }
                  onChange={(e) =>
                    setSelectedEmployeeId(
                      Number(
                        e.target.value
                      )
                    )
                  }
                >
                  <option value="">
                    Select Staff Member
                  </option>

                  {employeesList.map(
                    (emp) => (
                      <option
                        key={
                          emp.employeeId
                        }
                        value={
                          emp.employeeId
                        }
                      >
                        {emp.name} (
                        {emp.phoneNumber ||
                          "No contact"}
                        )
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() =>
                  setIsAssignModalOpen(
                    false
                  )
                }
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={
                  isAssigning ||
                  !selectedEmployeeId
                }
                onClick={
                  handleAssignSubmit
                }
                className="px-4 py-2 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 cursor-pointer"
              >
                {isAssigning
                  ? "Assigning..."
                  : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          RESCHEDULE MODAL
          ====================================================== */}
      {isRescheduleModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-4">

            <h3 className="text-lg font-bold text-slate-800">
              Reschedule Booking
            </h3>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                New Date
              </label>

              <input
                type="date"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs"
                value={
                  rescheduleDate
                }
                onChange={(e) =>
                  setRescheduleDate(
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                New Start Time
              </label>

              <input
                type="time"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs"
                value={
                  rescheduleTime
                }
                onChange={(e) =>
                  setRescheduleTime(
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Remarks
              </label>

              <textarea
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs"
                rows={2}
                placeholder="Reason for rescheduling..."
                value={
                  actionRemarks
                }
                onChange={(e) =>
                  setActionRemarks(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() =>
                  setIsRescheduleModalOpen(
                    false
                  )
                }
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>

              <button
                disabled={
                  isRescheduling
                }
                onClick={
                  handleRescheduleSubmit
                }
                className="px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 disabled:opacity-50 cursor-pointer"
              >
                {isRescheduling
                  ? "Updating..."
                  : "Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          EDIT DURATION MODAL
          ====================================================== */}
      {isDurationModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-4">

            <h3 className="text-lg font-bold text-slate-800">
              Update Booking Duration
            </h3>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                New End Time
              </label>

              <input
                type="time"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs"
                value={
                  newEndTime
                }
                onChange={(e) =>
                  setNewEndTime(
                    e.target.value
                  )
                }
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Remarks
              </label>

              <textarea
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs"
                rows={2}
                placeholder="Remarks..."
                value={
                  actionRemarks
                }
                onChange={(e) =>
                  setActionRemarks(
                    e.target.value
                  )
                }
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                onClick={() =>
                  setIsDurationModalOpen(
                    false
                  )
                }
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>

              <button
                disabled={
                  isUpdatingDuration
                }
                onClick={
                  handleDurationSubmit
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
              >
                {isUpdatingDuration
                  ? "Updating..."
                  : "Save Duration"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookingService;